import React, { useState, useEffect, useCallback } from 'react';
import { neonQuery, neonRawSelect } from '../services/api';
import { toTitleCase } from '../utils/format';

const PAGE_SIZE = 50;

function StarBadge({ score }) {
  const n = Number(score ?? 0);
  if (!n) return <span style={{ color: 'var(--gray-400)', fontSize: 11 }}>—</span>;
  const color = n >= 4.5 ? '#1a8a4a' : n >= 3.5 ? '#b06a00' : '#c0392b';
  return (
    <span style={{ fontWeight: 700, fontSize: 13, color }}>
      {n.toFixed(n % 1 ? 1 : 0)} ★
    </span>
  );
}

function SourceBadge({ source }) {
  const meta = {
    'legacy_pre_bc': { label: 'Legacy', bg: 'var(--gray-200)', fg: 'var(--gray-700)' },
    'yotpo': { label: 'Yotpo', bg: '#e6f1fa', fg: '#1268a8' },
    'manual_entry': { label: 'Manual', bg: '#fff4e0', fg: '#b06a00' },
  }[source] || { label: source || '—', bg: 'var(--gray-100)', fg: 'var(--gray-600)' };
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: 0.3, padding: '2px 6px',
      borderRadius: 3, background: meta.bg, color: meta.fg, textTransform: 'uppercase',
    }}>{meta.label}</span>
  );
}

// How many customers opened Google, Facebook or Trustpilot from the order feedback
// page's thank-you screen in the last 30 days. A tap is not a posted review: it says
// they went to the site with their words copied, and nothing about what they did there.
function PublicReviewTaps() {
  const [rows, setRows] = useState(null);
  useEffect(() => { neonQuery('order_review_taps_summary', { days: 30 }).then(setRows).catch(() => setRows([])); }, []);
  if (!rows) return null;
  const by = Object.fromEntries(rows.map((r) => [r.site, r]));
  const sites = [['google', 'Google'], ['facebook', 'Facebook'], ['trustpilot', 'Trustpilot']];
  const total = rows.reduce((n, r) => n + r.customers, 0);
  return (
    <div className="card" style={{ padding: '12px 16px', marginBottom: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)' }}>Sent to public review sites, last 30 days</div>
      <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', marginTop: 8 }}>
        {sites.map(([k, label]) => {
          const r = by[k] || { customers: 0, happy: 0, unhappy: 0 };
          return (
            <div key={k}>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--navy)' }}>{r.customers}</div>
              <div style={{ fontSize: 12, color: 'var(--gray-600)' }}>{label}</div>
              {r.customers > 0 && <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{r.happy} rated 4-5, {r.unhappy} rated 1-2</div>}
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 8 }}>
        {total === 0 ? 'Nobody has tapped one yet. Counting started Oct 13, 2026. ' : ''}
        Customers who tapped a site from the order feedback thank-you screen. A tap means they opened the site, not that they posted.
      </div>
    </div>
  );
}

export default function Reviews({ navigateTo }) {
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters (applied on Search click, not live)
  const [draftSearch, setDraftSearch] = useState('');
  const [draftSource, setDraftSource] = useState('');
  const [draftMinRating, setDraftMinRating] = useState('');
  const [draftMaxRating, setDraftMaxRating] = useState('');
  const [draftHasComment, setDraftHasComment] = useState('');
  const [draftAttribution, setDraftAttribution] = useState('');
  const [draftProductId, setDraftProductId] = useState('');

  // Applied filter set (what was last fetched)
  const [filters, setFilters] = useState({});

  // Drilldown state
  const [drilldownProductId, setDrilldownProductId] = useState(null);
  const [drilldown, setDrilldown] = useState(null);

  // BC product list (for the product picker dropdown)
  const [bcProducts, setBcProducts] = useState([]);

  // Expanded row id (show full comment + provenance)
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    neonQuery('reviews_admin_list', {
      ...filters,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    })
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  // Load BC products for the dropdown (cheap — 127 rows)
  useEffect(() => {
    neonRawSelect('SELECT bc_product_id, name FROM bc_products_snapshot WHERE is_visible IS NOT FALSE ORDER BY name')
      .then(({ rows }) => setBcProducts(rows || []))
      .catch(() => {});
  }, []);

  // Drilldown fetch
  useEffect(() => {
    if (!drilldownProductId) { setDrilldown(null); return; }
    neonQuery('reviews_admin_by_product', { productId: drilldownProductId })
      .then(setDrilldown)
      .catch(() => setDrilldown(null));
  }, [drilldownProductId]);

  const applyFilters = () => {
    const next = {};
    if (draftSearch) next.search = draftSearch;
    if (draftSource) next.source = draftSource;
    if (draftMinRating) next.minRating = draftMinRating;
    if (draftMaxRating) next.maxRating = draftMaxRating;
    if (draftHasComment) next.hasComment = draftHasComment;
    if (draftAttribution) next.attribution = draftAttribution;
    if (draftProductId) next.productId = draftProductId;
    setPage(1);
    setFilters(next);
  };

  const clearFilters = () => {
    setDraftSearch(''); setDraftSource(''); setDraftMinRating(''); setDraftMaxRating('');
    setDraftHasComment(''); setDraftAttribution(''); setDraftProductId('');
    setPage(1);
    setFilters({});
  };

  const rows = data?.items || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>
          Customer Reviews
        </h2>
        <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 4 }}>
          Browse + filter customer_reviews from Neon. {total > 0 && <strong>{total.toLocaleString()}</strong>} {total > 0 && 'matching rows.'}
        </div>
      </div>
      <PublicReviewTaps />

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header">Filters</div>
        <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>SEARCH (comment / name / email)</div>
            <input value={draftSearch} onChange={e => setDraftSearch(e.target.value)} placeholder="search..." className="form-input" style={{ fontSize: 11, width: 220 }} onKeyDown={e => e.key === 'Enter' && applyFilters()} />
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>SOURCE</div>
            <select value={draftSource} onChange={e => setDraftSource(e.target.value)} className="form-input" style={{ fontSize: 11, width: 140 }}>
              <option value="">All</option>
              <option value="legacy_pre_bc">Legacy (pre-BC)</option>
              <option value="yotpo">Yotpo</option>
              <option value="manual_entry">Manual entry</option>
            </select>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>MIN ★</div>
            <select value={draftMinRating} onChange={e => setDraftMinRating(e.target.value)} className="form-input" style={{ fontSize: 11, width: 70 }}>
              <option value="">—</option>
              {[1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>MAX ★</div>
            <select value={draftMaxRating} onChange={e => setDraftMaxRating(e.target.value)} className="form-input" style={{ fontSize: 11, width: 70 }}>
              <option value="">—</option>
              {[1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>HAS COMMENT</div>
            <select value={draftHasComment} onChange={e => setDraftHasComment(e.target.value)} className="form-input" style={{ fontSize: 11, width: 110 }}>
              <option value="">Any</option>
              <option value="true">With text</option>
              <option value="false">Rating only</option>
            </select>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>ATTRIBUTION</div>
            <select value={draftAttribution} onChange={e => setDraftAttribution(e.target.value)} className="form-input" style={{ fontSize: 11, width: 140 }}>
              <option value="">Any</option>
              <option value="attributed">Attributed to product</option>
              <option value="unattributed">Unattributed</option>
            </select>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>BC PRODUCT</div>
            <select value={draftProductId} onChange={e => setDraftProductId(e.target.value)} className="form-input" style={{ fontSize: 11, width: 240 }}>
              <option value="">All products</option>
              {bcProducts.map(p => <option key={p.bc_product_id} value={p.bc_product_id}>{p.name} ({p.bc_product_id})</option>)}
            </select>
          </div>
          <button onClick={applyFilters} className="btn btn-primary" style={{ fontSize: 11 }}>Search</button>
          <button onClick={clearFilters} className="btn btn-secondary" style={{ fontSize: 11 }}>Clear</button>
        </div>
      </div>

      {/* Drilldown panel — shown when a product filter is active */}
      {filters.productId && drilldown?.aggregate && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div className="card-header">
            Drilldown: {drilldown.product?.name || `Product ${filters.productId}`}
          </div>
          <div style={{ padding: 16, display: 'flex', gap: 32, flexWrap: 'wrap', fontSize: 12 }}>
            <Stat label="Average" value={<StarBadge score={drilldown.aggregate.rating_value} />} />
            <Stat label="Total reviews" value={drilldown.aggregate.rating_count?.toLocaleString()} />
            <Stat label="With comment" value={drilldown.aggregate.comment_count?.toLocaleString()} />
            <Stat label="Yotpo" value={drilldown.aggregate.yotpo_count?.toLocaleString()} />
            <Stat label="Legacy" value={drilldown.aggregate.legacy_count?.toLocaleString()} />
            <div style={{ flex: 1, minWidth: 280 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 6 }}>DISTRIBUTION</div>
              {[5,4,3,2,1].map(s => {
                const n = drilldown.aggregate[`${['','one','two','three','four','five'][s]}_star`] || 0;
                const total = drilldown.aggregate.rating_count || 1;
                const pct = (n / total) * 100;
                return (
                  <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3, fontSize: 11 }}>
                    <span style={{ width: 24, color: 'var(--gray-700)', fontWeight: 600 }}>{s}★</span>
                    <div style={{ flex: 1, height: 8, background: 'var(--gray-100)', borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: s >= 4 ? '#1a8a4a' : s === 3 ? '#b06a00' : '#c0392b' }} />
                    </div>
                    <span style={{ width: 60, textAlign: 'right', color: 'var(--gray-600)' }}>{n.toLocaleString()}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">Reviews{total > 0 && ` (showing ${rows.length} of ${total.toLocaleString()})`}</div>
        {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
        {error && <div style={{ padding: 16, fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}
        {!loading && !error && rows.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No reviews match these filters.</div>
        )}
        {rows.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)', width: 90 }}>Date</th>
                  <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)', width: 50 }}>★</th>
                  <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Customer</th>
                  <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)', width: 130 }}>Location</th>
                  <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Comment</th>
                  <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Product</th>
                  <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)', width: 70 }}>Source</th>
                  <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)', width: 60 }}>Verified</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <React.Fragment key={r.id}>
                    <tr onClick={() => setExpandedId(expandedId === r.id ? null : r.id)} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)', cursor: 'pointer' }}>
                      <td style={{ padding: '8px 12px', color: 'var(--gray-600)', whiteSpace: 'nowrap' }}>
                        {r.date_published ? new Date(r.date_published).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                      </td>
                      <td style={{ padding: '8px 8px', textAlign: 'center' }}><StarBadge score={r.overall_rating} /></td>
                      <td style={{ padding: '8px 8px', color: 'var(--navy)' }}>
                        {toTitleCase(r.customer_name) || <span style={{ color: 'var(--gray-400)' }}>(anon)</span>}
                        {r.is_vip && (
                          <img
                            src="https://cdn11.bigcommerce.com/s-3mvkyi71x6/content/images/vip-badge-square-v5.png"
                            alt="VIP Loyalty Club Member"
                            title="VIP Loyalty Club Member"
                            style={{ height: 18, width: 'auto', marginLeft: 6, verticalAlign: 'middle' }}
                          />
                        )}
                      </td>
                      <td style={{ padding: '8px 8px', color: 'var(--gray-600)', fontSize: 11 }}>
                        {r.location || <span style={{ color: 'var(--gray-400)' }}>—</span>}
                      </td>
                      <td style={{ padding: '8px 8px', color: 'var(--gray-700)', maxWidth: 360 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {r.comment || <span style={{ color: 'var(--gray-400)', fontStyle: 'italic' }}>(no comment)</span>}
                        </div>
                      </td>
                      <td style={{ padding: '8px 8px', color: 'var(--gray-600)', fontSize: 11 }}>
                        {r.product_names && r.product_names.length > 0
                          ? r.product_names.slice(0, 2).join(', ') + (r.product_names.length > 2 ? `, +${r.product_names.length - 2}` : '')
                          : <span style={{ color: 'var(--gray-400)' }}>—</span>}
                      </td>
                      <td style={{ padding: '8px 8px', textAlign: 'center' }}><SourceBadge source={r.source} /></td>
                      <td style={{ padding: '8px 8px', textAlign: 'center', fontSize: 14 }}>
                        {r.verified_buyer ? <span style={{ color: '#1a8a4a' }}>✓</span> : <span style={{ color: 'var(--gray-300)' }}>—</span>}
                      </td>
                    </tr>
                    {expandedId === r.id && (
                      <tr style={{ background: '#fff7ee', borderBottom: '1px solid var(--gray-200)' }}>
                        <td colSpan={8} style={{ padding: '12px 16px' }}>
                          {r.comment && (
                            <div style={{ marginBottom: 8, fontSize: 13, color: 'var(--gray-700)', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                              {r.comment}
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 11, color: 'var(--gray-600)' }}>
                            <span><b>id</b> {r.id}</span>
                            <span><b>legacy_feed_id</b> {r.legacy_feed_id || '—'}</span>
                            <span><b>order</b> {r.bc_order_id || '—'}</span>
                            <span><b>email</b> {r.customer_email || '—'}</span>
                            <span><b>display_name</b> {r.display_name || '—'} <span style={{ color: 'var(--gray-400)' }}>(public)</span></span>
                            <span><b>bc_product_ids</b> {r.bc_product_ids?.join(', ') || '—'}</span>
                          </div>
                          <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 11, color: 'var(--gray-600)' }}>
                            <Sub label="product" v={r.product_rating} />
                            <Sub label="packaging" v={r.packaging_rating} />
                            <Sub label="delivery" v={r.delivery_rating} />
                            <Sub label="service" v={r.service_rating} />
                            <Sub label="ease" v={r.ease_rating} />
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, padding: 12 }}>
            <button onClick={() => setPage(1)} disabled={page === 1} className="btn btn-navy" style={{ fontSize: 11 }}>« First</button>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="btn btn-navy" style={{ fontSize: 11 }}>← Prev</button>
            <span style={{ fontSize: 11, alignSelf: 'center', color: 'var(--gray-600)' }}>Page {page} of {totalPages.toLocaleString()}</span>
            <button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages} className="btn btn-navy" style={{ fontSize: 11 }}>Next →</button>
            <button onClick={() => setPage(totalPages)} disabled={page >= totalPages} className="btn btn-navy" style={{ fontSize: 11 }}>Last »</button>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={{ minWidth: 80 }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>{label.toUpperCase()}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)' }}>{value || '—'}</div>
    </div>
  );
}

function Sub({ label, v }) {
  if (v == null) return null;
  return <span><b>{label}</b> {v}★</span>;
}
